import axios from 'axios';
import { message } from 'antd';

const storage = typeof window !== 'undefined' ? window.sessionStorage : null;

class ApiClientError extends Error {
  constructor({ message: errorMessage, kind, status, code, details, originalError }) {
    super(errorMessage);
    this.name = 'ApiClientError';
    this.kind = kind;
    this.status = status;
    this.code = code;
    this.details = details;
    this.originalError = originalError;
  }
}

const resolveBaseUrl = () => {
  if (process.env.REACT_APP_API_BASE_URL) {
    return process.env.REACT_APP_API_BASE_URL;
  }

  return '/api/admin';
};

const legacyAdminPaths = new Set([
  '/dashboard',
  '/users',
  '/risk-control',
  '/help-center',
  '/memberships',
  '/rebates',
  '/withdrawals',
  '/invites',
  '/notifications',
  '/operations',
  '/curated-products',
  '/order-tracking',
  '/system-config',
  '/taobao-integration',
]);

const shouldRedirectToLogin = (pathname = '/') => {
  if (pathname === '/login') {
    return false;
  }

  if (pathname === '/admin' || pathname.startsWith('/admin/')) {
    return true;
  }

  return legacyAdminPaths.has(pathname);
};

const client = axios.create({
  baseURL: resolveBaseUrl(),
  timeout: 10000,
});

const shouldNotify = (config) => !config?.silentError && !config?.skipGlobalErrorHandler;

const notifyError = (config, text) => {
  if (shouldNotify(config) && text) {
    message.error(text);
  }
};

const buildApiClientError = ({
  message: errorMessage,
  kind,
  status = null,
  code = null,
  details = null,
  originalError = null,
}) => {
  return new ApiClientError({
    message: errorMessage,
    kind,
    status,
    code,
    details,
    originalError,
  });
};

const extractMessage = (data, fallback) => {
  return data?.message || data?.error || fallback;
};

const redirectToLogin = () => {
  storage?.removeItem('admin_token');
  storage?.removeItem('admin_info');
  if (typeof window !== 'undefined' && shouldRedirectToLogin(window.location.pathname)) {
    window.location.href = '/login';
  }
};

const normalizeHttpError = (error) => {
  if (axios.isCancel(error)) {
    return buildApiClientError({
      message: '请求已取消',
      kind: 'cancelled',
      originalError: error,
    });
  }

  if (error.code === 'ECONNABORTED') {
    return buildApiClientError({
      message: '请求超时，请稍后重试',
      kind: 'timeout',
      originalError: error,
    });
  }

  if (!error.response) {
    return buildApiClientError({
      message: '网络连接失败，请检查网络后重试',
      kind: 'network',
      originalError: error,
    });
  }

  const { status, data } = error.response;
  const common = {
    status,
    code: data?.code ?? null,
    details: data,
    originalError: error,
  };

  if (status === 401) {
    return buildApiClientError({
      ...common,
      message: extractMessage(data, '登录已过期，请重新登录'),
      kind: 'auth',
    });
  }

  if (status === 403) {
    return buildApiClientError({
      ...common,
      message: extractMessage(data, '没有权限执行该操作'),
      kind: 'forbidden',
    });
  }

  if (status === 404) {
    return buildApiClientError({
      ...common,
      message: extractMessage(data, '请求的资源不存在'),
      kind: 'not_found',
    });
  }

  if (status === 409) {
    return buildApiClientError({
      ...common,
      message: extractMessage(data, '数据已变化，请刷新后重试'),
      kind: 'conflict',
    });
  }

  if (status === 422) {
    return buildApiClientError({
      ...common,
      message: extractMessage(data, '提交的数据格式不正确'),
      kind: 'validation',
    });
  }

  if (status === 429) {
    return buildApiClientError({
      ...common,
      message: extractMessage(data, '请求过于频繁，请稍后再试'),
      kind: 'rate_limit',
    });
  }

  if (status >= 500) {
    return buildApiClientError({
      ...common,
      message: extractMessage(data, '服务暂时不可用，请稍后再试'),
      kind: 'server',
    });
  }

  return buildApiClientError({
    ...common,
    message: extractMessage(data, `请求失败 (${status})`),
    kind: 'http',
  });
};

// 请求拦截器
client.interceptors.request.use(
  (config) => {
    const token = storage?.getItem('admin_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// 响应拦截器
client.interceptors.response.use(
  (response) => {
    const { data, config } = response;

    if (!data || typeof data !== 'object' || !Object.prototype.hasOwnProperty.call(data, 'code')) {
      return data;
    }

    if (data.code !== 200) {
      const apiError = buildApiClientError({
        message: extractMessage(data, '请求失败'),
        kind: 'business',
        status: response.status,
        code: data.code,
        details: data,
      });
      notifyError(config, apiError.message);
      return Promise.reject(apiError);
    }
    return data.data;
  },
  (error) => {
    const normalizedError = error instanceof ApiClientError ? error : normalizeHttpError(error);

    if (normalizedError.kind === 'auth') {
      redirectToLogin();
    }

    notifyError(error.config, normalizedError.message);
    return Promise.reject(normalizedError);
  }
);

export default client;
export { ApiClientError, shouldRedirectToLogin };
